/* eslint-disable max-lines */
import { useTranslation } from 'react-i18next';
import { css, StyleSheet } from 'aphrodite';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import cn from 'classnames';
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  PricePlan,
  PricePlanTier
} from '@utils/types/back-office/price-plans-bo.ts';
import Input from '@components/atoms/ui-kit/input.tsx';
import Loader from '@components/atoms/loaders/loader.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import { useUpdatePricePlanBo } from '@utils/api/back-office/price-plans-bo/use-update-price-plan-bo.tsx';
import { Plus, Trash2 } from 'lucide-react';

type Props = {
  setNewName: Dispatch<SetStateAction<string>>;
  setNewPricePerUnit: Dispatch<SetStateAction<string>>;
  setNewTiers: React.Dispatch<React.SetStateAction<PricePlanTier[]>>;
  pricePlan: PricePlan | undefined;
  pricePlanIsLoading: boolean;
  isEditModalOpen: boolean;
  setIsEditModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  newName: string;
  newPricePerUnit: string;
  newTiers: PricePlanTier[];
  selectedPricePlan: number | undefined;
};

export default function UpdatePricePlanBo({
  setNewName,
  setNewPricePerUnit,
  setNewTiers,
  pricePlan,
  pricePlanIsLoading,
  isEditModalOpen,
  setIsEditModalOpen,
  newTiers,
  newName,
  newPricePerUnit,
  selectedPricePlan
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [tiers, updateTiers] = useState<PricePlanTier[]>([
    { start: 0, end: null, price_per_unit: '' }
  ]);

  const { mutateAsync: updatePricePlanAsync } = useUpdatePricePlanBo(
    selectedPricePlan || 0
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (pricePlan) {
      setNewName(pricePlan.result.name);
      setNewTiers(
        pricePlan.result.tiers.map(tier => ({
          start: tier.start,
          end: tier.end ?? null,
          price_per_unit: tier.price_per_unit
        }))
      );
      updateTiers(
        pricePlan.result.tiers.map(tier => ({
          start: tier.start,
          end: tier.end ?? null,
          price_per_unit: tier.price_per_unit
        }))
      );
    }
  }, [pricePlan, setNewName, setNewTiers, setNewPricePerUnit]);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        mainContainer: { borderBottom: `2px solid ${getColour('border')}` },
        mainContainerColor: { backgroundColor: getColour('secondLayer') },
        iconsColor: { color: getColour('icon') },
        mainText: { color: getColour('normalText') }
      }),
    [getColour]
  );

  const handleTierChange = <K extends keyof PricePlanTier>(
    index: number,
    key: K,
    value: PricePlanTier[K]
  ) => {
    const newArr = [...tiers];
    newArr[index][key] = value;
    updateTiers(newArr);
    setNewTiers(newArr);
  };

  const handleAddTier = () => {
    const lastTier = tiers[tiers.length - 1];
    // @ts-expect-error -- tier.start is typed as string | number at runtime
    const newTierStart = lastTier.start + 2;

    const updated = tiers.map((tier, i) => {
      if (i === tiers.length - 1 && tier.end === null) {
        return {
          ...tier,
          end: tiers.length === 1 ? newTierStart : newTierStart - 1
        };
      }
      return tier;
    });

    const newTier = { start: newTierStart, end: null, price_per_unit: '' };
    const newArr = [...updated, newTier];
    updateTiers(newArr);
    setNewTiers(newArr);
  };

  const handleRemoveTier = (index: number) => {
    const newArr = tiers.filter((_, i) => i !== index);
    updateTiers(newArr);
    setNewTiers(newArr);
  };

  const submitUpdate = async () => {
    setIsSubmitting(true);
    try {
      await updatePricePlanAsync({
        name: newName,
        tiers: newTiers,
        price_per_unit: newPricePerUnit
      });
      setIsSubmitting(false);
      setIsEditModalOpen(false);
    } catch {
      setIsSubmitting(false);
      // errors/toasts handled by hook; keep modal open
    }
  };

  const FORM_ID = 'update-price-plan-form';

  return (
    <Modal
      isOpen={isEditModalOpen}
      onClose={() => setIsEditModalOpen(false)}
      size="medium"
      title={t('update-price-plan') + ' ' + pricePlan?.result.name}
      caption={t('priceplans-desc')}
      footerActions={[
        <Button
          onClick={() => setIsEditModalOpen(false)}
          label={t('cancel')}
          size="small"
          variant="smoke"
          key="cancel"
        />,
        <Button
          label={isSubmitting ? t('saving') : t('save')}
          size="small"
          variant="primary"
          type="submit"
          form={FORM_ID}
          key="save"
        />
      ]}
    >
      <div className="overflow-y-auto max-h-[60vh] sm:max-h-96">
        {pricePlanIsLoading ? (
          <div className="h-full w-full flex justify-center items-center py-8">
            <Loader height={10} width={3} />
          </div>
        ) : (
          <div
            className={cn(
              'space-y-4',
              css(styles.mainContainerColor, styles.mainText)
            )}
          >
            <form
              id={FORM_ID}
              className="space-y-4"
              onSubmit={e => {
                e.preventDefault();
                submitUpdate();
              }}
            >
              {/* hidden submit so pressing Enter works anywhere inside */}
              <button type="submit" className="hidden" aria-hidden="true" />

              {/* Price Plan Name */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium mb-2"
                >
                  {t('price-plan-name')}
                </label>
                <Input
                  id="name"
                  defaultValue={pricePlan?.result.name}
                  onChange={e => setNewName(e.target.value)}
                  type="text"
                  autoComplete="name"
                  className="w-full"
                />
              </div>

              {/* Tiers */}
              <div className="space-y-4">
                {tiers.map((tier, index) => (
                  <div
                    key={index}
                    className="border rounded-2xl p-4 space-y-3"
                    style={{ borderColor: getColour('border') }}
                  >
                    {/* Tier Header */}
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold">
                        {t('tier')} {index + 1}
                      </h4>
                      {tiers.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveTier(index)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                          aria-label={t('delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {/* Tier Fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label
                          htmlFor={`tier_start_${index}`}
                          className="block text-xs sm:text-sm font-medium mb-1"
                        >
                          {t('start')}
                        </label>
                        <Input
                          id={`tier_start_${index}`}
                          defaultValue={tier.start || 0}
                          onChange={e =>
                            handleTierChange(
                              index,
                              'start',
                              Number(e.target.value)
                            )
                          }
                          type="number"
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor={`tier_end_${index}`}
                          className="block text-xs sm:text-sm font-medium mb-1"
                        >
                          {t('tier-end')}
                        </label>
                        <Input
                          id={`tier_end_${index}`}
                          value={
                            tier.end !== null
                              ? tier.end
                              : index === tiers.length - 1
                                ? '∞'
                                : ''
                          }
                          placeholder={
                            tier.end !== null
                              ? tier?.end?.toString()
                              : index === tiers.length - 1
                                ? '∞'
                                : ''
                          }
                          onChange={e => {
                            const value = e.target.value;
                            handleTierChange(
                              index,
                              'end',
                              value ? parseInt(value, 10) : null
                            );
                          }}
                          type="number"
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor={`price_unit_${index}`}
                          className="block text-xs sm:text-sm font-medium mb-1"
                        >
                          {t('price-per-unit')}
                        </label>
                        <Input
                          id={`price_unit_${index}`}
                          value={
                            tier.price_per_unit
                              ? Number(tier.price_per_unit)
                              : ''
                          }
                          onChange={e =>
                            handleTierChange(
                              index,
                              'price_per_unit',
                              e.target.value
                            )
                          }
                          type="number"
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Tier Button */}
              <div className="flex justify-end pt-2">
                <Button
                  type="button"
                  variant="light"
                  size="small"
                  onClick={handleAddTier}
                  icon={<Plus className="h-4 w-4" />}
                  label={t('add-tier')}
                />
              </div>
            </form>
          </div>
        )}
      </div>
    </Modal>
  );
}
